import { useCallback } from 'react';
import { Button } from '@mui/material';
import { FileUpload } from '@mui/icons-material';

import { UploadFile } from 'types';
import { useAlert } from 'hooks/alert';
import { parseAxiosError } from 'utils/error';

import { UploadFile as UploadFileComponent } from '../upload-file';

import { ImportResponse, CsvImportButtonProps } from './types';

export const CsvImportButton = <T extends ImportResponse | void = void>({
  tenantId,
  canImport,
  onSuccess,
  entityName,
  isImporting,
  triggerImport,
  formatSuccessMessage,
}: CsvImportButtonProps<T>) => {
  const { showErrorAlert, showSuccessAlert } = useAlert();

  const handleImportFile = useCallback(
    async (file: UploadFile | null) => {
      if (!file || !canImport || !tenantId) return;

      const formData = new FormData();
      formData.append('file', file.file);

      try {
        const result = await triggerImport({
          tenantId,
          data: formData,
        }).unwrap();

        if (formatSuccessMessage) {
          showSuccessAlert(formatSuccessMessage(result));
        } else if (
          result &&
          typeof result === 'object' &&
          'imported' in result
        ) {
          let resultText = `Imported ${result.imported} ${entityName}.`;
          if (result.duplicates) {
            resultText += ` Duplicates: ${result.duplicates}.`;
          }
          showSuccessAlert(resultText);
        }

        onSuccess?.();
      } catch (error) {
        showErrorAlert(parseAxiosError(error));
      }
    },
    [
      canImport,
      tenantId,
      triggerImport,
      entityName,
      onSuccess,
      formatSuccessMessage,
      showErrorAlert,
      showSuccessAlert,
    ]
  );

  if (!canImport) return null;

  return (
    <UploadFileComponent
      withoutCrop
      error={null}
      allowedFileSize={5}
      setFile={handleImportFile}
      accept={{ 'text/csv': ['.csv'] }}
      renderTrigger={(openDialog) => (
        <Button
          variant="contained"
          onClick={openDialog}
          disabled={isImporting}
          startIcon={<FileUpload />}
        >
          Import CSV
        </Button>
      )}
    />
  );
};
